import { useRef } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export type ScheduleViewMode = 'week' | 'day'

const outlineButton = 'inline-flex min-h-11 items-center justify-center gap-2 rounded-[10px] border border-[#DDE4DC] bg-white text-sm font-semibold text-[#14231B] hover:bg-[#F4F9F5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14231B] focus-visible:ring-offset-1'

/** Range + navigation, Day / Week, date picker and the location / category filters. */
export function SchedulingToolbar({
  rangeLabel,
  viewMode,
  pickedDate,
  isFetching,
  locationFilter,
  categoryFilter,
  locationOptions,
  categoryOptions,
  onViewModeChange,
  onMove,
  onToday,
  onPickDate,
  onLocationFilterChange,
  onCategoryFilterChange,
}: {
  rangeLabel: string
  viewMode: ScheduleViewMode
  pickedDate: string
  isFetching: boolean
  locationFilter: string
  categoryFilter: string
  locationOptions: string[]
  categoryOptions: string[]
  onViewModeChange: (mode: ScheduleViewMode) => void
  onMove: (direction: -1 | 1) => void
  onToday: () => void
  onPickDate: (date: string) => void
  onLocationFilterChange: (value: string) => void
  onCategoryFilterChange: (value: string) => void
}) {
  const calendarDateRef = useRef<HTMLInputElement | null>(null)
  const unit = viewMode === 'week' ? 'week' : 'day'

  const openCalendarPicker = () => {
    if (!calendarDateRef.current) {
      return
    }
    if (typeof calendarDateRef.current.showPicker === 'function') {
      calendarDateRef.current.showPicker()
      return
    }
    calendarDateRef.current.click()
  }

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-[14px] border border-[#DDE4DC] bg-white px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={cn(outlineButton, 'w-11')} onClick={() => onMove(-1)} aria-label={`Previous ${unit}`}>
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <h2 className="min-w-[190px] text-center text-[17px] font-bold text-[#14231B]" aria-live="polite">{rangeLabel}</h2>
        <button type="button" className={cn(outlineButton, 'w-11')} onClick={() => onMove(1)} aria-label={`Next ${unit}`}>
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
        <button type="button" className={cn(outlineButton, 'px-4')} onClick={onToday}>
          Today
        </button>
        <button type="button" className={cn(outlineButton, 'px-3')} onClick={openCalendarPicker} aria-label="Pick a start date">
          <CalendarDays className="h-4 w-4" aria-hidden="true" />
          Pick date
        </button>
        <Input
          ref={calendarDateRef}
          type="date"
          value={pickedDate}
          onChange={(event) => {
            if (event.target.value) onPickDate(event.target.value)
          }}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>

      <div className="flex gap-1 rounded-xl bg-[#F3F5F1] p-1" role="group" aria-label="View">
        {(['day', 'week'] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => onViewModeChange(mode)}
            aria-pressed={viewMode === mode}
            className={cn(
              'min-h-10 rounded-[9px] px-[18px] text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14231B]',
              viewMode === mode ? 'bg-white font-bold text-[#14231B] shadow-[0_1px_2px_rgba(20,35,27,0.18)]' : 'font-medium text-[#3C4B43] hover:text-[#14231B]',
            )}
          >
            {mode === 'day' ? 'Day' : 'Week'}
          </button>
        ))}
      </div>

      {isFetching ? (
        <span className="inline-flex items-center gap-1 text-xs text-[#4A5A51]" role="status">
          <Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" aria-hidden="true" />
          Updating…
        </span>
      ) : null}

      <div className="flex w-full flex-wrap gap-2 sm:ml-auto sm:w-auto">
        <Input
          type="search"
          list="schedule-location-options"
          value={locationFilter}
          onChange={(event) => onLocationFilterChange(event.target.value)}
          placeholder="All locations"
          aria-label="Filter by location"
          className="h-11 w-full rounded-[10px] border-[#DDE4DC] bg-white text-sm sm:w-44"
        />
        <datalist id="schedule-location-options">
          {locationOptions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
        <Input
          type="search"
          list="schedule-category-options"
          value={categoryFilter}
          onChange={(event) => onCategoryFilterChange(event.target.value)}
          placeholder="All categories"
          aria-label="Filter by category"
          className="h-11 w-full rounded-[10px] border-[#DDE4DC] bg-white text-sm sm:w-44"
        />
        <datalist id="schedule-category-options">
          {categoryOptions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
      </div>
    </div>
  )
}
